"use client";

import {
  ArrowClockwiseIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  CalendarIcon,
  CalendarXIcon,
  CellSignalLowIcon,
  ChatCircleTextIcon,
  CheckCircleIcon,
  CircleIcon,
  ClipboardIcon,
  ClockIcon,
  CopyIcon,
  EnvelopeIcon,
  GhostIcon,
  GlobeSimpleIcon,
  InfoIcon,
  MagnifyingGlassIcon,
  FileMagnifyingGlassIcon,
  MinusCircleIcon,
  PaperPlaneTiltIcon,
  PauseCircleIcon,
  PhoneIcon,
  QuestionIcon,
  RecordIcon,
  ShieldPlusIcon,
  SignInIcon,
  SignOutIcon,
  SirenIcon,
  TableIcon,
  TimerIcon,
  UserCircleIcon,
  WarningCircleIcon,
  XCircleIcon,
} from "@phosphor-icons/react";
import { UserSearchIcon } from "lucide-react";

import { AppointmentStatus } from "@/packages/shared/src/models/appointment";
import { AppointmentCreatedBy } from "@/src/models/appointment";
import { PriorAuthorizationStatus } from "@/src/models/documents/prior-authorization/prior-authorization";
import { PriorAuthorizationCommsStatus } from "@/src/models/documents/prior-authorization/prior-authorization-comms";
import { ReferralStatus, SchedulingPriority } from "@/src/models/documents/referral";
import { LocationRole } from "@/src/models/location";
import { PatientEngagementType, PatientEngagementDirection } from "@/src/models/patient-engagement";
import { PractitionerRole } from "@/src/models/practitioner";
import { TaskPriority, TaskStatus, TaskType } from "@/src/models/task";
import { CallerIntent, CallOutcome } from "@/src/models/transcript-evaluation";

import type { Icon } from "@phosphor-icons/react";

export const defaultStatusIconAndColor = { icon: QuestionIcon, color: "text-muted-foreground/70" };

export const defaultTypeIconAndColor = { icon: QuestionIcon, color: "text-muted-foreground/70" };

export const defaultReferralStatusIconAndColor = { icon: QuestionIcon, color: "text-muted-foreground/70" };

export const defaultPriorAuthorizationStatusIconAndColor = { icon: QuestionIcon, color: "text-muted-foreground/70" };

export const defaultPriorAuthorizationCommsStatusIconAndColor = {
  icon: QuestionIcon,
  color: "text-muted-foreground/70",
};

export const defaultAppointmentStatusIconAndColor = { icon: QuestionIcon, color: "text-muted-foreground/70" };

export const defaultCallOutcomeIconAndColor = { icon: QuestionIcon, color: "text-muted-foreground/70" };

/** Agent-authored call outcome when an appointment was booked on the call. */
export const appointmentBookedOutcomeIcon = { icon: CheckCircleIcon, color: "text-success" };

/** Judge-authored terminal call outcome (`transcript_evaluation.call_outcome`). */
export const callOutcomeIcons: Record<CallOutcome, { icon: Icon; color: string }> = {
  [CallOutcome.COMPLETED]: { icon: CheckCircleIcon, color: "text-success" },
  [CallOutcome.ESCALATED]: { icon: SignOutIcon, color: "text-amber-500/70" },
  [CallOutcome.ABANDONED]: { icon: MinusCircleIcon, color: "text-muted-foreground/70" },
};

// Caller-intent category icons: intents share one icon per category (scheduling / question /
// people-routing / fallback) so the Calls table scans by group without asking users to learn 18
// distinct icon–color pairs. Soft tints only — saturated status colors stay reserved for Outcome.
const scheduleIntentIcon = { icon: CalendarIcon, color: "text-sky-500/70" };
const questionIntentIcon = { icon: InfoIcon, color: "text-violet-500/70" };
const peopleRoutingIntentIcon = { icon: ChatCircleTextIcon, color: "text-emerald-500/70" };
const fallbackIntentIcon = { icon: MinusCircleIcon, color: "text-muted-foreground/50" };

/** Judge-authored caller intent (`transcript_evaluation.caller_intent`), keyed by the full enum
 * so adding an intent forces a category assignment here (compile error otherwise). */
export const callerIntentIcons: Record<CallerIntent, { icon: Icon; color: string }> = {
  // Scheduling
  [CallerIntent.SCHEDULE_INITIAL]: scheduleIntentIcon,
  [CallerIntent.SCHEDULE_ESTABLISHED]: scheduleIntentIcon,
  [CallerIntent.RESCHEDULE]: scheduleIntentIcon,
  [CallerIntent.CANCEL]: scheduleIntentIcon,
  [CallerIntent.CONFIRM]: scheduleIntentIcon,
  // Questions / requests
  [CallerIntent.BILLING_OR_PRICING]: questionIntentIcon,
  [CallerIntent.PRESCRIPTION_REFILL]: questionIntentIcon,
  [CallerIntent.CLINICAL_QUESTION]: questionIntentIcon,
  [CallerIntent.INSURANCE_QUESTION]: questionIntentIcon,
  [CallerIntent.AUTHORIZATION_OR_REFERRAL_QUESTION]: questionIntentIcon,
  [CallerIntent.FORMS_OR_MEDICAL_RECORDS]: questionIntentIcon,
  [CallerIntent.TELEHEALTH_OR_CONNECTION_HELP]: questionIntentIcon,
  // People-routing
  [CallerIntent.MESSAGE_FOR_STAFF]: peopleRoutingIntentIcon,
  [CallerIntent.LANGUAGE_SUPPORT]: peopleRoutingIntentIcon,
  [CallerIntent.PROVIDER_TRANSFER]: peopleRoutingIntentIcon,
  [CallerIntent.REQUESTED_HUMAN]: peopleRoutingIntentIcon,
  // Fallbacks
  [CallerIntent.UNCONFIRMED]: fallbackIntentIcon,
  [CallerIntent.OTHER]: fallbackIntentIcon,
};

export const taskTypeMetadata = {
  [TaskType.INTAKE]: { icon: FileMagnifyingGlassIcon, color: "text-emerald-500/70" },
  [TaskType.PATIENT_INTAKE]: { icon: ClipboardIcon, color: "text-amber-500/70" },
  [TaskType.WELCOME_CALL]: { icon: PhoneIcon, color: "text-muted-foreground" },
  [TaskType.SCHEDULE]: { icon: CalendarIcon, color: "text-sky-500/70" },
  [TaskType.ELIGIBILITY_CHECK]: { icon: ShieldPlusIcon, color: "text-muted-foreground" },
  [TaskType.REFERRAL_AUTHORIZATION]: { icon: ShieldPlusIcon, color: "text-amber-500/70" },
  [TaskType.PRIOR_AUTHORIZATION]: {
    icon: ShieldPlusIcon,
    color: "text-fuchsia-500/70",
  },
  [TaskType.CLASSIFY_DOCUMENT]: { icon: MagnifyingGlassIcon, color: "text-cyan-500/70" },
  [TaskType.REFERRAL]: { icon: TableIcon, color: "text-lime-300/70" },
  [TaskType.DOC_MANAGEMENT]: { icon: CopyIcon, color: "text-blue-500/70" },
  [TaskType.LEAD_FORM]: { icon: CopyIcon, color: "text-blue-500/70" },
  [TaskType.PATIENT_LOOKUP]: { icon: UserSearchIcon, color: "text-purple-500/70" },
  [TaskType.VERIFY_APPOINTMENT]: { icon: CalendarIcon, color: "text-emerald-500/70" },
  [TaskType.VOICE_QA]: { icon: PhoneIcon, color: "text-violet-500/70" },
  [TaskType.SCHEDULING_ELIGIBILITY_VERIFICATION]: { icon: CalendarIcon, color: "text-sky-500/70" },
};

export const statusIcons = {
  [TaskStatus.AWAITING_INFORMATION]: { icon: InfoIcon, color: "text-yellow-500/70" },
  [TaskStatus.BACKLOG]: { icon: CircleIcon, color: "text-muted-foreground/70" },
  [TaskStatus.BLOCKED]: { icon: WarningCircleIcon, color: "text-warn" },
  [TaskStatus.CANCELLED]: { icon: XCircleIcon, color: "text-red-500/70" },
  [TaskStatus.COMPLETED]: { icon: CheckCircleIcon, color: "text-blue-500/70" },
  [TaskStatus.DUPLICATE]: { icon: CopyIcon, color: "text-muted-foreground/70" },
  [TaskStatus.FAILED]: { icon: XCircleIcon, color: "text-red-500/70" },
  [TaskStatus.NOT_RELEVANT]: { icon: MinusCircleIcon, color: "text-muted-foreground/70" },
  [TaskStatus.PAUSED]: { icon: PauseCircleIcon, color: "text-muted-foreground/70" },
  [TaskStatus.PROCESSING]: { icon: TimerIcon, color: "text-muted-foreground/70" },
  [TaskStatus.REVIEW_NEEDED]: { icon: InfoIcon, color: "text-yellow-500/70" },
  [TaskStatus.REVERT]: { icon: ArrowClockwiseIcon, color: "text-muted-foreground/70" },
  [TaskStatus.SHADOW]: { icon: GhostIcon, color: "text-muted-foreground/70" },
  [TaskStatus.VERIFICATION_NEEDED]: { icon: InfoIcon, color: "text-orange-500/70" },
};

export const workflowCreatedIcon = ClockIcon;

export function getClockColorByDate(dateString: string | null): string {
  if (!dateString) {
    return "text-gray-500";
  }

  const now = new Date();
  const date = new Date(dateString);
  const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

  if (diffInHours > 48) return "text-red-500/70";
  if (diffInHours > 24) return "text-amber-500/70";

  return "text-sky-500/70";
}

export const referralStatusIcons = {
  [ReferralStatus.AWAITING_INFORMATION]: { icon: InfoIcon, color: "text-yellow-500/70" },
  [ReferralStatus.CANCELLED]: { icon: XCircleIcon, color: "text-destructive" },
  [ReferralStatus.COMPLETED]: { icon: CheckCircleIcon, color: "text-success" },
  [ReferralStatus.DUPLICATE]: { icon: CopyIcon, color: "text-muted-foreground/70" },
  [ReferralStatus.EXPIRED]: { icon: CalendarXIcon, color: "text-warn" },
  [ReferralStatus.IN_OUTREACH]: { icon: PaperPlaneTiltIcon, color: "text-blue-500" },
  [ReferralStatus.IN_PROGRESS]: { icon: RecordIcon, color: "text-teal-500" },
  [ReferralStatus.NOT_STARTED]: { icon: ClockIcon, color: "text-amber-500" },
};

export const priorAuthorizationStatusIcons = {
  [PriorAuthorizationStatus.APPROVED]: { icon: CheckCircleIcon, color: "text-success" },
  [PriorAuthorizationStatus.DENIED]: { icon: XCircleIcon, color: "text-destructive" },
  [PriorAuthorizationStatus.FOLLOW_UP]: { icon: PaperPlaneTiltIcon, color: "text-blue-500" },
  [PriorAuthorizationStatus.NO_AUTH_REQUIRED]: { icon: MinusCircleIcon, color: "text-muted-foreground/70" },
  [PriorAuthorizationStatus.NOT_RECEIVED]: { icon: EnvelopeIcon, color: "text-muted-foreground/70" },
  [PriorAuthorizationStatus.OTHER]: { icon: QuestionIcon, color: "text-muted-foreground/70" },
  [PriorAuthorizationStatus.PARTIAL_APPROVAL]: { icon: WarningCircleIcon, color: "text-warn" },
  [PriorAuthorizationStatus.PENDING]: { icon: ClockIcon, color: "text-amber-500" },
  [PriorAuthorizationStatus.REVIEW_NEEDED]: { icon: PauseCircleIcon, color: "text-amber-500" },
  [PriorAuthorizationStatus.ESCALATED]: { icon: SirenIcon, color: "text-destructive" },
};

export const priorAuthorizationCommsStatusIcons = {
  [PriorAuthorizationCommsStatus.SENT]: { icon: CheckCircleIcon, color: "text-success" },
  [PriorAuthorizationCommsStatus.FAILED]: { icon: XCircleIcon, color: "text-destructive" },
};

export const appointmentStatusIcons = {
  [AppointmentStatus.SCHEDULED]: { icon: CalendarIcon, color: "text-sky-500/70" },
  [AppointmentStatus.CHECKED_IN]: { icon: TimerIcon, color: "text-blue-500/70" },
  [AppointmentStatus.COMPLETED]: { icon: CheckCircleIcon, color: "text-success" },
  [AppointmentStatus.CANCELLED]: { icon: XCircleIcon, color: "text-destructive" },
  [AppointmentStatus.NO_SHOW]: { icon: WarningCircleIcon, color: "text-warn" },
  [AppointmentStatus.RESCHEDULED]: { icon: ArrowClockwiseIcon, color: "text-primary" },
  [AppointmentStatus.UNKNOWN]: { icon: QuestionIcon, color: "text-muted-foreground/70" },
};

export const appointmentCreatedByMetadata: Record<AppointmentCreatedBy, { color: string; icon: Icon; label: string }> =
  {
    [AppointmentCreatedBy.EXTERNAL]: {
      label: "External",
      icon: GlobeSimpleIcon,
      color: "text-slate-500/80",
    },
    [AppointmentCreatedBy.VALERIE_SMS]: {
      label: "Valerie SMS",
      icon: ChatCircleTextIcon,
      color: "text-emerald-500/70",
    },
    [AppointmentCreatedBy.VALERIE_VOICE]: {
      label: "Valerie Voice",
      icon: PhoneIcon,
      color: "text-sky-500/70",
    },
    [AppointmentCreatedBy.VALERIE_WEB]: {
      label: "Valerie Web",
      icon: GlobeSimpleIcon,
      color: "text-violet-500/70",
    },
  };

export const priorityIcons = {
  [TaskPriority.LOW]: { icon: CellSignalLowIcon, color: "text-muted-foreground/50" },
  [TaskPriority.MEDIUM]: null,
  [TaskPriority.HIGH]: { icon: WarningCircleIcon, color: "text-warn" },
};

export const schedulingPriorityIcons = {
  [SchedulingPriority.ROUTINE]: null,
  [SchedulingPriority.URGENT]: { icon: WarningCircleIcon, color: "text-warn" },
  [SchedulingPriority.STAT]: { icon: SirenIcon, color: "text-destructive" },
};

export const patientEngagementTypeIcons = {
  [PatientEngagementType.SMS]: { icon: ChatCircleTextIcon, color: "text-blue-500/70" },
  [PatientEngagementType.Phone]: { icon: PhoneIcon, color: "text-green-500/70" },
  [PatientEngagementType.Email]: { icon: EnvelopeIcon, color: "text-purple-500/70" },
  [PatientEngagementType.Online]: { icon: GlobeSimpleIcon, color: "text-cyan-500/70" },
  [PatientEngagementType.InPerson]: { icon: UserCircleIcon, color: "text-amber-500/70" },
};

export const patientEngagementDirectionIcons = {
  [PatientEngagementDirection.Inbound]: { icon: ArrowDownIcon, color: "text-muted-foreground" },
  [PatientEngagementDirection.Outbound]: { icon: ArrowUpIcon, color: "text-muted-foreground" },
};

export const practitionerRoleIcons: Record<PractitionerRole, { icon: Icon; color: string }> = {
  [PractitionerRole.REFERRING_PROVIDER]: { icon: SignInIcon, color: "text-muted-foreground" },
  [PractitionerRole.RECEIVING_PROVIDER]: { icon: SignOutIcon, color: "text-muted-foreground" },
  [PractitionerRole.ORDERING_PROVIDER]: { icon: ClipboardIcon, color: "text-muted-foreground" },
  [PractitionerRole.PRIMARY_CARE_PROVIDER]: { icon: UserCircleIcon, color: "text-muted-foreground" },
  [PractitionerRole.UNKNOWN_PROVIDER]: { icon: QuestionIcon, color: "text-muted-foreground" },
};

export const locationRoleIcons: Record<LocationRole, { icon: Icon; color: string }> = {
  [LocationRole.REFERRING_LOCATION]: { icon: SignInIcon, color: "text-muted-foreground" },
  [LocationRole.RECEIVING_LOCATION]: { icon: SignOutIcon, color: "text-muted-foreground" },
};
